import React from "react";
import { MapPin, Mail, MessageCircle, Instagram, Facebook, Lock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT, IMAGES } from "../data/site";

export const Footer = () => {
  const { t } = useLang();
  const waLink = `https://wa.me/${CONTACT.whatsapp}`;

  const links = [
    { href: "#rooms", label: t.nav.rooms },
    { href: "#gallery", label: t.nav.gallery },
    { href: "#included", label: t.nav.included },
    { href: "#story", label: t.nav.story },
    { href: "#location", label: t.nav.location },
  ];

  return (
    <footer className="relative overflow-hidden bg-purpleink-deep grain" data-testid="site-footer">
      <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-white p-0.5">
                <img src={IMAGES.logo} alt="Casa Romano" className="h-full w-full rounded-full object-cover" />
              </span>
              <div className="flex flex-col leading-none">
                <span className="font-display text-2xl font-bold text-white">Casa Romano</span>
                <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-marigold">
                  San Juan del Sur
                </span>
              </div>
            </div>
            <p className="mt-6 max-w-sm text-base leading-relaxed text-white/70">
              {t.footer.tagline}
            </p>
            <div className="mt-6 flex gap-3">
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <h4 className="text-xs font-semibold uppercase tracking-[0.25em] text-marigold">
              {t.footer.linksTitle}
            </h4>
            <ul className="mt-5 flex flex-col gap-3">
              {links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    className="text-sm text-white/75 transition-colors hover:text-white"
                    data-testid={`footer-link-${l.href.slice(1)}`}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs font-semibold uppercase tracking-[0.25em] text-marigold">
              {t.footer.contactTitle}
            </h4>
            <ul className="mt-5 flex flex-col gap-4">
              <li>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-sm text-white/75 transition-colors hover:text-white"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-teal" />
                  {CONTACT.whatsappDisplay}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="flex items-start gap-3 text-sm text-white/75 transition-colors hover:text-white"
                  data-testid="footer-email"
                >
                  <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-teal" />
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a
                  href={CONTACT.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-sm text-white/75 transition-colors hover:text-white"
                  data-testid="footer-maps"
                >
                  <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-teal" />
                  Zona Central, San Juan del Sur, Nicaragua
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-3 border-t border-white/12 pt-8 sm:flex-row">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} Casa Romano. {t.footer.rights}
          </p>
          <div className="flex items-center gap-5">
            <a
              href="/admin"
              data-testid="footer-owner-login"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-white/55 transition-colors hover:text-marigold"
            >
              <Lock className="h-3.5 w-3.5" />
              {t.footer.ownerLogin}
            </a>
            <a
              href="https://www.GregMiceli.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-credit"
              className="text-xs text-white/45 transition-colors hover:text-marigold"
            >
              Website Created by www.GregMiceli.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
